import { parseISO, format } from 'date-fns';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { PayRecord, Timecard, TimecardDay } from '../types';
import { resolveWorkHourlyRate } from './laborCost';
import {
  resolvePositionName,
  resolveScheduleName,
  type TimecardAnalysisLookupMaps,
} from './timecardAnalysisReport';
import { formatCompanyDate, formatCompanyDateTime } from './companyDateFormat';

/** String matrix for CSV/Excel/PDF export from saved snapshot rows. */
export function earlyLateDataRowsToStringRows(dataRows: EarlyLateReportRow[]): string[][] {
  return dataRows.map((r) => [
    r.dateDisplay,
    r.firstName,
    r.lastName,
    r.location,
    r.schedule,
    r.position,
    r.shiftDisplay,
    r.clockIn,
    String(r.lateClockInMins),
    r.clockOut,
    String(r.earlyClockOutMins),
    String(r.netMinutes),
    r.amount.toFixed(2),
  ]);
}

export interface EarlyLateReportRow {
  date: string;
  dateDisplay: string;
  firstName: string;
  lastName: string;
  location: string;
  schedule: string;
  position: string;
  shiftDisplay: string;
  clockIn: string;
  lateClockInMins: number;
  clockOut: string;
  earlyClockOutMins: number;
  netMinutes: number;
  amount: number;
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = parseISO(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return format(d, 'HH:mm');
  } catch {
    return '—';
  }
}

/** Shift as "07:00 - 16:00" from scheduled in/out. */
export function formatShiftRange(scheduledIn?: string | null, scheduledOut?: string | null): string {
  if (!scheduledIn || !scheduledOut) return '—';
  return `${formatTime(scheduledIn)} - ${formatTime(scheduledOut)}`;
}

/**
 * Late = minutes actualIn is after scheduledIn (0 if no actual or on time).
 * Early out = minutes actualOut is before scheduled shift end (0 if none).
 */
export function computeLateAndEarlyMinutes(day: TimecardDay): {
  lateClockInMins: number;
  earlyClockOutMins: number;
  netMinutes: number;
} {
  if (!day.scheduledIn || !day.scheduledOut) {
    return { lateClockInMins: 0, earlyClockOutMins: 0, netMinutes: 0 };
  }
  const schedIn = parseISO(day.scheduledIn);
  const schedOut = parseISO(day.scheduledOut);
  let schedOutMs = schedOut.getTime();
  const schedInMs = schedIn.getTime();
  if (schedOutMs <= schedInMs) {
    schedOutMs += 24 * 60 * 60 * 1000;
  }

  let lateIn = 0;
  if (day.actualIn) {
    const actualIn = parseISO(day.actualIn);
    lateIn = Math.max(0, Math.round((actualIn.getTime() - schedInMs) / 60000));
  }

  let earlyOut = 0;
  if (day.actualOut) {
    const actualOut = parseISO(day.actualOut);
    earlyOut = Math.max(0, Math.round((schedOutMs - actualOut.getTime()) / 60000));
  }

  return {
    lateClockInMins: lateIn,
    earlyClockOutMins: earlyOut,
    netMinutes: lateIn + earlyOut,
  };
}

function compareYmd(a: string, b: string): number {
  return a.localeCompare(b);
}

export interface EarlyLateReportResult {
  headers: string[];
  rows: string[][];
  dataRows: EarlyLateReportRow[];
  recordsProcessed: number;
  totalAmount: number;
}

/** Legacy default when older snapshots did not store <see cref="netMinutesMin" />. */
export const LEGACY_EARLY_LATE_NET_MINUTES_MIN = 5;

/**
 * Only rows with net late/early minutes strictly greater than {@code netMinutesMinThreshold} are included.
 * @param netMinutesMinThreshold Excluded rows have netMinutes &lt;= this (use 0 to include any positive net time).
 */
export function buildEarlyLateReport(
  timecards: Timecard[],
  payRecordMap: Record<number, PayRecord>,
  locationMap: Record<number, string>,
  fromDate: string,
  toDate: string,
  currencyLabel: string,
  netMinutesMinThreshold = 0,
  lookupMaps?: TimecardAnalysisLookupMaps
): EarlyLateReportResult {
  const out: EarlyLateReportRow[] = [];

  for (const tc of timecards || []) {
    const emp = tc.employee;
    const firstName = emp?.firstName ?? '';
    const lastName = emp?.lastName ?? '';
    const locName =
      tc.locationId != null ? locationMap[tc.locationId] ?? `Location #${tc.locationId}` : '—';
    const payRecord = payRecordMap[tc.employeeId];

    for (const day of tc.days || []) {
      if (!day.date) continue;
      if (compareYmd(day.date, fromDate) < 0 || compareYmd(day.date, toDate) > 0) continue;

      const { lateClockInMins, earlyClockOutMins, netMinutes } = computeLateAndEarlyMinutes(day);
      if (netMinutes <= netMinutesMinThreshold) continue;

      const hourly = resolveWorkHourlyRate(payRecord, {
        locationId: tc.locationId,
        positionId: day.positionId,
        scheduleId: day.scheduleId,
      });
      const amount = Math.round((netMinutes / 60) * hourly * 100) / 100;

      out.push({
        date: day.date,
        dateDisplay: formatCompanyDate(day.date) || day.date,
        firstName,
        lastName,
        location: locName,
        schedule: resolveScheduleName(day, lookupMaps) || '—',
        position: resolvePositionName(day, lookupMaps) || '—',
        shiftDisplay: formatShiftRange(day.scheduledIn, day.scheduledOut),
        clockIn: formatTime(day.actualIn),
        lateClockInMins,
        clockOut: formatTime(day.actualOut),
        earlyClockOutMins,
        netMinutes,
        amount,
      });
    }
  }

  out.sort((a, b) => {
    const c = compareYmd(a.date, b.date);
    if (c !== 0) return c;
    return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
  });

  const headers = [
    'Date',
    'First name',
    'Last name',
    'Location',
    'Schedule',
    'Position',
    'Shift',
    'Clock In',
    'Late Clock In (Mins)',
    'Clock Out',
    'Early Clock Out (Mins)',
    'Net Time (Mins)',
    `Amount (${currencyLabel})`,
  ];

  const rows = earlyLateDataRowsToStringRows(out);

  const totalAmount = Math.round(out.reduce((s, r) => s + r.amount, 0) * 100) / 100;

  return {
    headers,
    rows,
    dataRows: out,
    recordsProcessed: out.length,
    totalAmount,
  };
}

export function createEarlyLatePdfBlob(
  headers: string[],
  rows: string[][],
  meta: {
    fromDate: string;
    toDate: string;
    recordsProcessed: number;
    totalAmount: number;
    generatedAt: Date;
    currencyLabel: string;
    netMinutesMin?: number;
  }
): Blob {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  doc.setFontSize(11);
  doc.text('Early / Late Report', 14, 12);
  doc.setFontSize(9);
  const netLine =
    meta.netMinutesMin != null ? `  |  Net time (min.) > ${meta.netMinutesMin}` : '';
  doc.text(
    `Run: ${formatCompanyDateTime(meta.generatedAt)}  |  From ${formatCompanyDate(meta.fromDate)} to ${formatCompanyDate(meta.toDate)}  |  Records: ${meta.recordsProcessed}  |  Total (${meta.currencyLabel}): ${meta.totalAmount.toFixed(2)}${netLine}`,
    14,
    18
  );
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 24,
    styles: { fontSize: 7 },
    headStyles: { fillColor: [60, 60, 60] },
    margin: { left: 14, right: 14 },
  });
  const totalY = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? 24;
  doc.setFontSize(9);
  doc.text(`Grand total (${meta.currencyLabel}): ${meta.totalAmount.toFixed(2)}`, 14, totalY + 8);
  return doc.output('blob');
}
